/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Slice to handle CRUD operation for Store of Dedicated Delivery.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { StoreDataArgs } from "../../../pages/dedicatedDelivery/DdTypes";
import agent from "../../api/agent";
import {
  BulkStoreIngestionResponse,
  DdStorePerformanceUIResponse,
} from "../../models/ApiResponse";
import {
  AddStoreRequest,
  EditStoreRequest,
} from "../../models/requestModels/StoreRequest";
import { DdStorePerformanceResponse } from "../../models/responseModels/DdStorePerformanceResponse";
import { BulkStoreIngestionWrapper } from "../../models/responseModels/BulkStoreIngestionWrapper";

// type required for initial state.
type InitialStoreState = {
  isLoading: boolean;
  storeData: DdStorePerformanceResponse[];
  errorMessage: string | null;
};

// initialize initial state
const initialState: InitialStoreState = {
  isLoading: false,
  storeData: [],
  errorMessage: null,
};

// argument type required for server call for data on store page
export interface StoreAddOrUpdateValidation {
  clientId: number;
  clientStoreId: string;
}

// thunk to get all stores per client
export const getStoreData = createAsyncThunk<
  DdStorePerformanceUIResponse,
  StoreDataArgs
>(
  "dedicated-delivery/overview-of-stores-response",
  async (storeDataArgs: StoreDataArgs) => {
    const response = await agent.DedicatedDeliveryAgent.getStoreData(
      storeDataArgs
    );
    return response;
  }
);

// thunk to persist new stores
export const persistNewStore = createAsyncThunk<any, AddStoreRequest>(
  "dedicated-delivery/persist-new-store-response",
  async (storeDataArgs: AddStoreRequest) => {
    const response = await agent.DedicatedDeliveryAgent.persistNewStoreData(
      storeDataArgs
    );
    return response;
  }
);

// get client information from server for general page
export const bulkStoreIngestion = createAsyncThunk<
  BulkStoreIngestionResponse,
  BulkStoreIngestionWrapper
>(
  "dedicated-delivery/persist-new-stores-response",
  async (args: BulkStoreIngestionWrapper) => {
    const response = await agent.DedicatedDeliveryAgent.bulkImportStores(args);
    return response;
  }
);

// thunk to update existing stores
export const editExistingStore = createAsyncThunk<any, EditStoreRequest>(
  "dedicated-delivery/edit-stores-response",
  async (storeDataArgs: EditStoreRequest) => {
    const response = await agent.DedicatedDeliveryAgent.editExistingStoreData(
      storeDataArgs
    );
    return response;
  }
);

// reducer to handle actions on store page
const dedicatedDeliveryStoreSlice = createSlice({
  name: "dedicatedDeliveryStore",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStoreData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getStoreData.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.storeData = action.payload.data;
        } else {
          state.storeData = [];
        }
      })
      .addCase(getStoreData.rejected, (state, action) => {
        state.isLoading = false;
        state.storeData = [];
        state.errorMessage = action.error.message!;
      });
  },
});

export default dedicatedDeliveryStoreSlice.reducer;
